/**
 * CockpitVerif — Vue dédiée à la branche Vérif.
 * Le débat est interne par design (loi 1971) : aucune bulle.
 * Fil d'activité dédupliqué par sujet (un sujet distinct = une seule ligne),
 * animation d'attente avec avatars du Conseil, compteur basé sur les sujets uniques.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import type { DeliberationHookResult, VerifActivity, VerifExchange } from "@/hooks/use-deliberation";
import { COUNCIL } from "@/lib/council";
import { resolvePersona } from "@/lib/persona-mapping";
import { NanaeLogoFull } from "@/components/qv3/NanaeLogo";
import { QLiveDot } from "@/components/qv3/QLiveDot";
import { NanaeDisclaimer } from "@/components/qorum/NanaeDisclaimer";
import { LLM_MODELS, type LLMPersonaId } from "@/components/brand/llm-logos";
import { CouncilCircle } from "@/components/qv3/CouncilCircle";
import { verdictProseStyle } from "@/lib/verdict-typography";

const COUNCIL_BY_NAME: Record<string, (typeof COUNCIL)[number]> = Object.fromEntries(
  COUNCIL.map((c) => [c.name.toLowerCase(), c]),
);
const COUNCIL_BY_ID: Record<string, (typeof COUNCIL)[number]> = Object.fromEntries(
  COUNCIL.map((c) => [c.id, c]),
);

/** Résout le conseiller depuis n'importe quel label backend (nom, rôle, modèle). */
function resolveCouncilor(raw: string): (typeof COUNCIL)[number] | undefined {
  const direct = COUNCIL_BY_NAME[raw.toLowerCase()];
  if (direct) return direct;
  const personaId = resolvePersona(raw);
  return personaId ? COUNCIL_BY_ID[personaId] : undefined;
}


const VERBS = ["examine", "analyse", "vérifie", "relève"] as const;

function hashVerb(seed: string): (typeof VERBS)[number] {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return VERBS[Math.abs(h) % VERBS.length];
}

function fmtTime(totalSec: number) {
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function normalizeSujet(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
}

interface DedupedRow extends VerifActivity {
  occurrences: number;
  lastSeen: number;
}

const WAITING_MESSAGES = [
  "Le Conseil prend connaissance de votre dossier…",
  "Les conseillers étudient les clauses…",
  "Lecture croisée du dossier en cours…",
  "Le Conseil rassemble ses notes…",
  "Examen attentif des pièces transmises…",
];

interface Props {
  state: DeliberationHookResult;
}

export function CockpitVerif({ state }: Props) {
  const navigate = useNavigate();
  const [elapsed, setElapsed] = useState(0);
  const [waitingMsgIdx, setWaitingMsgIdx] = useState(0);
  const streamRef = useRef<HTMLDivElement | null>(null);
  const activities = state.verifActivities;

  useEffect(() => {
    if (state.isCompleted || state.meetingDone) return;
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [state.isCompleted, state.meetingDone]);

  useEffect(() => {
    const t = setInterval(() => {
      setWaitingMsgIdx((i) => (i + 1) % WAITING_MESSAGES.length);
    }, 3500);
    return () => clearInterval(t);
  }, []);

  // Dédup par sujet normalisé : chaque sujet distinct = une seule ligne.
  // Si un sujet revient, on incrémente occurrences + on met à jour lastSeen
  // (la ligne "pulse" alors brièvement), mais on ne crée pas de nouvelle entrée.
  const dedupedRows = useMemo<DedupedRow[]>(() => {
    const byKey = new Map<string, DedupedRow>();
    const order: string[] = [];
    for (const a of activities) {
      const key = normalizeSujet(a.sujet) || a.id;
      const existing = byKey.get(key);
      if (existing) {
        byKey.set(key, {
          ...existing,
          occurrences: existing.occurrences + 1,
          lastSeen: a.receivedAt,
          pointsReleves: Math.max(existing.pointsReleves, a.pointsReleves || 0),
        });
      } else {
        byKey.set(key, { ...a, occurrences: 1, lastSeen: a.receivedAt });
        order.push(key);
      }
    }
    return order.map((k) => byKey.get(k)!);
  }, [activities]);

  useEffect(() => {
    const el = streamRef.current;
    if (!el) return;
    try {
      if (typeof el.scrollTo === "function") {
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      } else {
        el.scrollTop = el.scrollHeight;
      }
    } catch {
      try { el.scrollTop = el.scrollHeight; } catch { /* noop */ }
    }
  }, [dedupedRows.length]);

  const uniqueCount = dedupedRows.length;

  const debateFinished = state.verdictReady || state.meetingDone || state.isCompleted;
  // Garde-fou : on n'affiche le bouton « Voir les observations » que lorsque
  // les observations sont réellement prêtes (clarityReady), pour ne jamais
  // mener l'utilisateur vers un écran vide.
  const observationsReady = state.clarityReady;

  // Auto-scroll de la fenêtre vers le bas dès qu'une nouvelle ligne d'activité
  // apparaît, et quand le débat se termine / les observations sont prêtes,
  // pour que l'utilisateur suive le fil et voie le bouton verdict.
  const exchangesCount = state.verifExchanges.length;
  useEffect(() => {
    const t = setTimeout(() => {
      window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });
    }, 200);
    return () => clearTimeout(t);
  }, [dedupedRows.length, exchangesCount, debateFinished, observationsReady]);

  function handleSeeObservations() {
    const vid = state.verdictId ?? state.id;
    if (!vid) return;
    navigate({ to: "/verdict/$id", params: { id: vid } });
  }

  return (
    <div className="qv3 qv3-dark qv3-cockpit">
      <style>{`
        @keyframes verif-row-in {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes verif-row-pulse {
          0%, 100% { background: transparent; }
          50% { background: rgba(255,23,68,0.10); }
        }
        @keyframes verif-avatar-breathe {
          0%, 100% { opacity: 0.55; transform: scale(1); box-shadow: 0 0 0 0 rgba(255,23,68,0); }
          50% { opacity: 1; transform: scale(1.08); box-shadow: 0 0 14px 2px rgba(255,23,68,0.35); }
        }
        @keyframes verif-avatar-active {
          0%, 100% { box-shadow: 0 0 18px 4px rgba(224,198,139,0.65), 0 0 36px 8px rgba(255,23,68,0.35); transform: scale(1.12); }
          50%      { box-shadow: 0 0 28px 8px rgba(224,198,139,0.95), 0 0 60px 16px rgba(255,23,68,0.55); transform: scale(1.18); }
        }
        @keyframes verif-msg-fade {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>


      <header className="qv3-cockpit-header">
        <div className="qv3-cockpit-brand">
          <Link to="/foyer" aria-label="Retour au Bureau" style={{ display: "inline-flex", textDecoration: "none", color: "inherit" }}>
            <NanaeLogoFull layout="horizontal" symbolSize="md" wordmarkSize="md" withKanji={false} />
          </Link>
        </div>
        <div className="qv3-cockpit-title">
          Le Conseil{" "}
          <span
            aria-label={debateFinished ? "Audit terminé" : "Audit en cours"}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              marginLeft: 4,
              padding: "2px 8px",
              border: `1px solid ${debateFinished ? "rgba(255,23,68,0.45)" : "rgba(220,80,80,0.55)"}`,
              borderRadius: 999,
              fontFamily: '"JetBrains Mono", monospace',
              fontStyle: "normal",
              fontSize: 10,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: debateFinished ? "#F5EFE3" : "#dc5050",
            }}
          >
            {!debateFinished && (
              <span
                aria-hidden
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "#dc5050",
                  boxShadow: "0 0 6px #dc5050",
                }}
              />
            )}
            Audit {debateFinished ? "· Terminé" : "· Live"}
          </span>
        </div>
        <div className="qv3-cockpit-meta">
          {!debateFinished && <QLiveDot label="LIVE" />}
          <span className="qv3-mono-hud is-caps size-xs">
            AUDIT · {fmtTime(elapsed)}
          </span>
          <Link
            to="/foyer"
            className="qv3-mono-hud is-caps size-xs"
            style={{
              marginLeft: 12,
              padding: "6px 12px",
              border: "1px solid #F5EFE3",
              color: "#F5EFE3",
              textDecoration: "none",
              letterSpacing: "0.08em",
            }}
          >
            ← Nouvelle saisine
          </Link>
        </div>
      </header>

      <main
        ref={streamRef}
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "32px 24px 80px",
          maxWidth: 760,
          width: "100%",
          margin: "0 auto",
          boxSizing: "border-box",
        }}
      >
        {/* Question */}
        <section style={{ marginBottom: 32 }}>
          <div
            style={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: 10,
              letterSpacing: 2,
              textTransform: "uppercase",
              color: "#F5EFE3",
              marginBottom: 10,
            }}
          >
            Le dossier soumis
          </div>
          <p
            style={{
              fontFamily: '"DM Sans", sans-serif',
              fontStyle: "normal",
              fontWeight: 500,
              fontSize: 17,
              lineHeight: 1.55,
              color: "#f3efe6",
              margin: 0,
              whiteSpace: "pre-wrap",
            }}
          >
            « {state.question} »
          </p>
        </section>

        <div style={{ margin: "0 auto 24px", display: "flex", justifyContent: "center" }}>
          <CouncilCircle size={420} />
        </div>



        <p
          style={{
            fontFamily: '"DM Sans", sans-serif',
            fontSize: 14,
            lineHeight: 1.6,
            color: "rgba(243, 239, 230, 0.92)",
            margin: "0 0 24px",
          }}
        >
          Le Conseil examine votre dossier en interne. Vous ne verrez pas les
          arguments du débat ; à la fin, vous recevrez une liste d'observations
          et les questions à poser à votre avocat, expert-comptable ou banquier.
        </p>

        <CouncilPulseStrip activities={activities} done={debateFinished} />


        {/* Fil d'activité */}
        <section aria-label="Fil d'activité du Conseil">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              marginBottom: 16,
            }}
          >
            <div
              style={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: 10,
                letterSpacing: 2,
                textTransform: "uppercase",
                color: "#F5EFE3",
              }}
            >
              Fil d'activité
            </div>
            <div
              style={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: 10,
                letterSpacing: 1.4,
                color: "rgba(245,239,227, 0.75)",
              }}
            >
              {uniqueCount === 0
                ? "Lecture en cours…"
                : `${uniqueCount} sujet${uniqueCount > 1 ? "s" : ""} relevé${uniqueCount > 1 ? "s" : ""}`}
            </div>
          </div>

          {(() => {
            const councilRows = buildCouncilSignals(state);
            const hasAnything = councilRows.length > 0 || dedupedRows.length > 0 || state.verifExchanges.length > 0;
            if (!hasAnything && !debateFinished) {
              return <WaitingChoreography message={WAITING_MESSAGES[waitingMsgIdx]} />;
            }
            if (!hasAnything) return null;
            return (
              <ol
                style={{
                  listStyle: "none",
                  margin: 0,
                  padding: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                {councilRows.map((row) => (
                  <CouncilSignalRow key={row.key} label={row.label} detail={row.detail} />
                ))}
                {dedupedRows.map((row) => (
                  <VerifActivityRow key={`${row.cadre}|${normalizeSujet(row.sujet)}`} row={row} />
                ))}
                {state.verifExchanges.map((ex) => (
                  <VerifExchangeRow key={ex.id} exchange={ex} />
                ))}
              </ol>
            );
          })()}
        </section>


        {/* CTA fin — visible uniquement quand les observations sont prêtes */}
        {debateFinished && (
          <div
            style={{
              marginTop: 40,
              padding: "24px 22px",
              border: "0.5px solid #FF1744",
              background: "rgba(255,23,68, 0.06)",
              display: "flex",
              flexDirection: "column",
              gap: 14,
              alignItems: "flex-start",
            }}
          >
            <p
              style={{
                ...verdictProseStyle,
                color: "#f3efe6",
                margin: 0,
              }}
            >
              {observationsReady
                ? "Le Conseil a terminé la vérification de votre dossier."
                : "Le Conseil finalise ses observations…"}
            </p>
            {observationsReady ? (
              <button
                type="button"
                onClick={handleSeeObservations}
                style={{
                  fontFamily: "Oswald, sans-serif",
                  fontWeight: 700,
                  fontSize: 12,
                  letterSpacing: 3,
                  textTransform: "uppercase",
                  padding: "14px 22px",
                  background: "#FF1744",
                  color: "#0a0a0a",
                  border: "0.5px solid #FF1744",
                  cursor: "pointer",
                }}
              >
                Voir les observations du Conseil →
              </button>
            ) : (
              <div
                style={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: 10,
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  color: "rgba(245,239,227, 0.75)",
                }}
              >
                Mise en forme en cours…
              </div>
            )}
          </div>
        )}
        <NanaeDisclaimer tone="dark" variant="verif" />
      </main>
    </div>
  );
}

interface CouncilSignal {
  key: string;
  label: string;
  detail?: string;
}

const SOURCE_LABEL: Record<string, string> = {
  web: "le web",
  presse: "la presse",
  scholar: "les travaux universitaires",
  linkedin: "LinkedIn",
  article: "le web",
  video: "le web",
};

function buildCouncilSignals(state: DeliberationHookResult): CouncilSignal[] {
  const rows: CouncilSignal[] = [];

  // 1. Recherches groupées par source-famille
  const bySource = new Map<string, number>();
  for (const q of state.researchQueries) {
    const src = String(q.source || "web").toLowerCase();
    bySource.set(src, (bySource.get(src) ?? 0) + 1);
  }
  if (bySource.size > 0) {
    const parts: string[] = [];
    for (const [src, n] of bySource) {
      const label = SOURCE_LABEL[src] ?? "le web";
      parts.push(`${label}${n > 1 ? ` (${n})` : ""}`);
    }
    rows.push({
      key: "council-research",
      label: "Le Conseil réunit les sources",
      detail: `Consulte ${parts.join(" · ")}`,
    });
  }

  // 2. Grounding juridique (déjà filtré côté hook : null si 0,0)
  if (state.verifGroundingLegal) {
    const { articles, decisions } = state.verifGroundingLegal;
    const bits: string[] = [];
    if (articles > 0) bits.push(`${articles} article${articles > 1 ? "s" : ""}`);
    if (decisions > 0) bits.push(`${decisions} décision${decisions > 1 ? "s" : ""}`);
    rows.push({
      key: "council-grounding",
      label: "Le Conseil consulte le droit",
      detail: bits.join(" · "),
    });
  }

  // 3. Fact-check — neutre, 0 confirmées n'est pas un échec
  if (state.verifFactcheck) {
    const { verifies, confirmees } = state.verifFactcheck;
    rows.push({
      key: "council-factcheck",
      label: `Le Conseil vérifie ${verifies} affirmation${verifies > 1 ? "s" : ""} sur le web`,
      detail:
        confirmees > 0
          ? `${confirmees} confirmée${confirmees > 1 ? "s" : ""} à ce stade`
          : undefined,
    });
  }

  return rows;
}

function CouncilSignalRow({ label, detail }: { label: string; detail?: string }) {
  return (
    <li
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: "10px 8px",
        borderBottom: "0.5px solid rgba(255,23,68, 0.18)",
        background: "rgba(255,23,68, 0.04)",
        animation: "verif-row-in 240ms ease-out",
      }}
    >
      <span
        aria-hidden
        style={{
          flex: "0 0 auto",
          width: 28,
          height: 28,
          borderRadius: "50%",
          border: "0.5px solid #FF1744",
          background: "transparent",
          color: "#F5EFE3",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: 9,
          letterSpacing: 1,
          textTransform: "uppercase",
          fontWeight: 600,
        }}
      >
        C
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          style={{
            margin: 0,
            fontFamily: '"DM Sans", sans-serif',
            fontSize: 14,
            lineHeight: 1.5,
            color: "#f3efe6",
          }}
        >
          {label}
        </p>
        {detail && (
          <p
            style={{
              margin: "4px 0 0",
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: 10,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: "rgba(245,239,227, 0.8)",
            }}
          >
            {detail}
          </p>
        )}
      </div>
    </li>
  );
}

function WaitingChoreography({ message }: { message: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 22,
        padding: "32px 0 16px",
      }}
    >
      <div style={{ display: "flex", gap: 10 }}>
        {COUNCIL.map((c, i) => {
          const Logo = LLM_MODELS[c.id as LLMPersonaId]?.logo;
          return (
            <span
              key={c.id}
              aria-hidden
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: c.color,
                color: "#f3efe6",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: '"Playfair Display", serif',
                fontSize: 13,
                fontWeight: 600,
                animation: `verif-avatar-breathe 1.8s ease-in-out ${i * 0.18}s infinite`,
              }}
            >
              {Logo ? <Logo width={16} height={16} /> : c.initial}
            </span>
          );
        })}
      </div>

      <p
        key={message}
        style={{
          ...verdictProseStyle,
          margin: 0,
          color: "#F5EFE3",
          opacity: 0.85,
          textAlign: "center",
          animation: "verif-msg-fade 600ms ease-out",
        }}
      >
        {message}
      </p>
    </div>
  );
}

function VerifActivityRow({ row }: { row: DedupedRow }) {
  const cadre = resolveCouncilor(row.cadre);
  const verb = hashVerb(row.cadre + "|" + row.sujet);
  const initial = cadre?.initial ?? row.cadre.slice(0, 1).toUpperCase();
  const accent = cadre?.color ?? "#FF1744";
  const active = row.occurrences > 1;
  const Logo = cadre ? LLM_MODELS[cadre.id as LLMPersonaId]?.logo : undefined;

  return (
    <li
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: "10px 8px",
        borderBottom: "0.5px solid rgba(243, 239, 230, 0.06)",
        animation: active
          ? "verif-row-in 240ms ease-out, verif-row-pulse 1.6s ease-in-out"
          : "verif-row-in 240ms ease-out",
      }}
    >
      <span
        aria-hidden
        style={{
          flex: "0 0 auto",
          width: 28,
          height: 28,
          borderRadius: "50%",
          background: accent,
          color: "#f3efe6",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: '"Playfair Display", serif',
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        {Logo ? <Logo width={15} height={15} /> : initial}
      </span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          style={{
            margin: 0,
            fontFamily: '"DM Sans", sans-serif',
            fontSize: 14,
            lineHeight: 1.5,
            color: "#f3efe6",
          }}
        >
          <strong style={{ fontWeight: 600, color: accent }}>{row.cadre}</strong>{" "}
          <span style={{ color: "rgba(243, 239, 230, 0.75)" }}>{verb}</span>{" "}
          <span>{row.sujet}</span>
        </p>
        <p
          style={{
            margin: "4px 0 0",
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 10,
            letterSpacing: 1.4,
            textTransform: "uppercase",
            color: "rgba(245,239,227, 0.7)",
          }}
        >
          {cadre?.model ?? "—"}
          {active && " · Toujours en cours"}
        </p>

      </div>
    </li>
  );
}

function CouncilPulseStrip({
  activities,
  done,
}: {
  activities: VerifActivity[];
  done: boolean;
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (done) return;
    const t = setInterval(() => setNow(Date.now()), 800);
    return () => clearInterval(t);
  }, [done]);

  const lastByCadre = useMemo(() => {
    const map = new Map<string, number>();
    for (const a of activities) {
      const k = a.cadre.toLowerCase();
      const prev = map.get(k) ?? 0;
      if (a.receivedAt > prev) map.set(k, a.receivedAt);
    }
    return map;
  }, [activities]);

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: 18,
        padding: "8px 0 28px",
        margin: "0 0 24px",
        borderBottom: "0.5px solid rgba(255,23,68, 0.18)",
      }}
    >
      {COUNCIL.map((c, i) => {
        const last = lastByCadre.get(c.name.toLowerCase()) ?? 0;
        const active = !done && last > 0 && now - last < 4500;
        const llm = LLM_MODELS[c.id as LLMPersonaId];
        const Logo = llm?.logo;
        return (
          <figure
            key={c.id}
            title={`${c.name} — ${c.role} · ${llm?.label ?? c.model}`}
            style={{
              margin: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 6,
              width: 92,
              textAlign: "center",
            }}
          >
            <span
              aria-hidden
              style={{
                width: 42,
                height: 42,
                borderRadius: "50%",
                background: c.color,
                color: "#f3efe6",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                border: active ? "1px solid #F5EFE3" : "0.5px solid rgba(255,23,68,0.35)",
                animation: done
                  ? "none"
                  : active
                    ? "verif-avatar-active 1.2s ease-in-out infinite"
                    : `verif-avatar-breathe 2.2s ease-in-out ${i * 0.18}s infinite`,
              }}
            >
              {Logo ? <Logo width={22} height={22} /> : <span style={{ fontFamily: '"Playfair Display", serif', fontSize: 14, fontWeight: 600 }}>{c.initial}</span>}
            </span>
            <figcaption
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                lineHeight: 1.15,
              }}
            >
              <span
                style={{
                  fontFamily: '"Playfair Display", serif',
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#f3efe6",
                }}
              >
                {c.name}
              </span>
              <span
                style={{
                  fontFamily: '"Cormorant Garamond", serif',
                  fontStyle: "italic",
                  fontSize: 10.5,
                  color: "rgba(243,239,230,0.72)",
                }}
              >
                {c.role}
              </span>
              <span
                style={{
                  fontFamily: '"DM Mono", monospace',
                  fontSize: 8.5,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: "rgba(245,239,227,0.9)",
                  marginTop: 2,
                }}
              >
                {llm?.label ?? c.model}
              </span>
            </figcaption>
          </figure>
        );
      })}
    </div>
  );
}


const EXCHANGE_VERBS = [
  "réagit au point de",
  "revient sur le point de",
  "échange avec",
] as const;

function hashExchangeVerb(seed: string): (typeof EXCHANGE_VERBS)[number] {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return EXCHANGE_VERBS[Math.abs(h) % EXCHANGE_VERBS.length];
}

function VerifExchangeRow({ exchange }: { exchange: VerifExchange }) {
  const fromC = resolveCouncilor(exchange.from);
  const toC = resolveCouncilor(exchange.to);
  const verb = hashExchangeVerb(exchange.from + "|" + exchange.to);
  const fromInitial = fromC?.initial ?? exchange.from.slice(0, 1).toUpperCase();
  const toInitial = toC?.initial ?? exchange.to.slice(0, 1).toUpperCase();
  const fromColor = fromC?.color ?? "#FF1744";
  const toColor = toC?.color ?? "#FF1744";

  return (
    <li
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: "10px 8px",
        borderBottom: "0.5px solid rgba(255,23,68, 0.18)",
        background: "rgba(255,23,68, 0.05)",
        borderLeft: "2px solid rgba(255,23,68, 0.55)",
        animation: "verif-row-in 240ms ease-out",
      }}
    >
      <span
        aria-hidden
        style={{
          flex: "0 0 auto",
          display: "inline-flex",
          alignItems: "center",
          gap: 2,
          paddingTop: 2,
        }}
      >
        <span
          style={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            background: fromColor,
            color: "#f3efe6",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: '"Playfair Display", serif',
            fontSize: 10,
            fontWeight: 600,
          }}
        >
          {fromInitial}
        </span>
        <span
          aria-hidden
          style={{
            color: "#F5EFE3",
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 14,
            lineHeight: 1,
          }}
        >
          ⇄
        </span>
        <span
          style={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            background: toColor,
            color: "#f3efe6",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: '"Playfair Display", serif',
            fontSize: 10,
            fontWeight: 600,
          }}
        >
          {toInitial}
        </span>
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          style={{
            margin: 0,
            fontFamily: '"DM Sans", sans-serif',
            fontSize: 14,
            lineHeight: 1.5,
            color: "#f3efe6",
          }}
        >
          <strong style={{ fontWeight: 600, color: fromColor }}>{exchange.from}</strong>{" "}
          <span style={{ color: "rgba(243, 239, 230, 0.75)" }}>{verb}</span>{" "}
          <strong style={{ fontWeight: 600, color: toColor }}>{exchange.to}</strong>
        </p>
        <p
          style={{
            margin: "4px 0 0",
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 10,
            letterSpacing: 1.4,
            textTransform: "uppercase",
            color: "rgba(245,239,227, 0.75)",
          }}
        >
          Confrontation
          {exchange.pointsConfrontes > 0 &&
            ` · ${exchange.pointsConfrontes} point${exchange.pointsConfrontes > 1 ? "s" : ""} confronté${exchange.pointsConfrontes > 1 ? "s" : ""}`}
        </p>
      </div>
    </li>
  );
}

export default CockpitVerif;
